import { useState, useEffect, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import "@/App.css";
import axios from "axios";
import { Shield, ArrowClockwise, DownloadSimple, WarningCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { ConfirmedProductionKpis } from "@/components/ConfirmedProductionKpis";
import { FinishedProductionTable } from "@/components/FinishedProductionTable";
import { OperationSummaryTable } from "@/components/OperationSummaryTable";
import { WipBadge } from "@/components/ConfirmationDetailTable";
import { formatUnit } from "../lib/formatUnit";
import {
  computeKpis, groupByOperation, groupFinishedByLot, splitOperation, isFinalConfirmation, formatQty,
} from "../lib/confirmedProduction";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatAt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN") : "—");
// Sep 15 2026, user's explicit ask: show Operation ID + description together
// WITH the Reporting Point ID, e.g. "OP_10 - BLANK (RP_10)".
const formatReportingPoint = (r) => {
  if (!r.reporting_point_description) return r.reporting_point_id || "—";
  return r.reporting_point_id ? `${r.reporting_point_description} (${r.reporting_point_id})` : r.reporting_point_description;
};

// Oct 22 2026, user's explicit ask: "BY DEFAULT REPORT OPEN ONLY 7 DAYS".
const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const defaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 6);
  return { from: localISO(from), to: localISO(to) };
};

// Oct 7 2026, real incident: a shop-floor account saw only "no transactions
// match these filters" after pasting a product code into the Reporting Point
// box - say which filter is hiding the rows instead of leaving it to guesswork.
function EmptyReportState({ loadedCount, productFilter, rpFilter, lotFilter, dateFrom, dateTo }) {
  const range = dateFrom || dateTo ? `${dateFrom || "the beginning"} to ${dateTo || "today"}` : "all dates";
  if (loadedCount > 0) {
    const active = [
      productFilter.trim() && `Output Product "${productFilter.trim()}"`,
      rpFilter.trim() && `Reporting Point "${rpFilter.trim()}"`,
      lotFilter?.trim() && `Lot ID "${lotFilter.trim()}"`,
    ].filter(Boolean);
    return (
      <div className="py-8 px-4 text-center space-y-1" data-testid="confirmed-report-empty-state">
        <p className="text-[13px] font-semibold text-[#344054]">
          {loadedCount.toLocaleString("en-IN")} transaction{loadedCount === 1 ? "" : "s"} loaded, none match {active.join(" + ")}
        </p>
        <p className="text-xs text-[#667085]">
          Reporting Point expects a step, not a product - e.g. <span className="font-mono">RP_10</span>,{" "}
          <span className="font-mono">END</span> or part of its name like <span className="font-mono">GALVANIZE</span>.
        </p>
      </div>
    );
  }
  return (
    <div className="py-8 px-4 text-center space-y-1" data-testid="confirmed-report-empty-state">
      <p className="text-[13px] text-[#667085]">No confirmed production was posted in {range}.</p>
    </div>
  );
}

const TRANSACTION_COLUMNS = ["Date/Time", "Lot ID", "Output Product", "Description", "Reporting Point", "Model ID", "Site", "Confirmed Qty", "UOM", "Scrap", "By-product Qty", "By-product Unit", "WIP Clearing", "Confirmed By"];

export default function ConfirmedProductionPage() {
  const [view, setView] = useState("finished");
  const [dateFrom, setDateFrom] = useState(defaultRange().from);
  const [dateTo, setDateTo] = useState(defaultRange().to);
  const [siteFilter, setSiteFilter] = useState("all");
  const [byproductFilter, setByproductFilter] = useState("all");
  const [wipFilter, setWipFilter] = useState("all");
  const [outputProductFilter, setOutputProductFilter] = useState("");
  const [reportingPointFilter, setReportingPointFilter] = useState("");
  // Oct 31 2026, user: "add filter by lot id".
  const [lotFilter, setLotFilter] = useState("");
  const [openAsOf, setOpenAsOf] = useState(null);
  const [knownSites, setKnownSites] = useState([]);
  const [rows, setRows] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/store-requests/known-sites`).then(({ data }) => setKnownSites(data.sites || [])).catch(() => {});
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await axios.get(`${API}/production-confirmation/confirmed-report`, {
        params: {
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          site_ids: siteFilter !== "all" ? siteFilter : undefined,
          byproduct: byproductFilter,
          wip: wipFilter,
        },
      });
      setRows(data.rows);
      setTruncated(!!data.truncated);
      setOpenAsOf(data.open_quantity_as_of || null);
    } catch (e) {
      setLoadError(e.response?.data?.detail || "Failed to load Confirmed Production report");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, siteFilter, byproductFilter, wipFilter]);

  useEffect(() => { loadReport(); }, [loadReport]);

  // Oct 22 2026, user's explicit ask: searching P27601 was also pulling in
  // P27601-1, a different product. A code that matches a product exactly wins
  // outright; a partial code still lists every candidate.
  const exactProductMatch = useMemo(() => {
    const q = outputProductFilter.trim().toLowerCase();
    if (!q) return null;
    return rows.find((r) => (r.main_output_product || "").toLowerCase() === q)?.main_output_product || null;
  }, [rows, outputProductFilter]);

  const visibleRows = useMemo(() => {
    let out = rows;
    if (outputProductFilter.trim()) {
      const q = outputProductFilter.trim().toLowerCase();
      out = exactProductMatch
        ? out.filter((r) => (r.main_output_product || "").toLowerCase() === q)
        : out.filter((r) => (r.main_output_product || "").toLowerCase().includes(q));
    }
    if (reportingPointFilter.trim()) {
      const q = reportingPointFilter.trim().toLowerCase();
      out = out.filter((r) => (r.reporting_point_id || "").toLowerCase().includes(q) || (r.reporting_point_description || "").toLowerCase().includes(q));
    }
    if (lotFilter.trim()) {
      const q = lotFilter.trim().toLowerCase();
      out = out.filter((r) => String(r.production_lot_id || "").toLowerCase().includes(q));
    }
    return out;
  }, [rows, outputProductFilter, reportingPointFilter, lotFilter, exactProductMatch]);

  const kpis = useMemo(() => computeKpis(visibleRows), [visibleRows]);
  const operationGroups = useMemo(() => groupByOperation(visibleRows), [visibleRows]);
  const finishedLots = useMemo(() => groupFinishedByLot(visibleRows), [visibleRows]);

  const clearFilters = () => {
    const range = defaultRange();
    setDateFrom(range.from);
    setDateTo(range.to);
    setSiteFilter("all");
    setByproductFilter("all");
    setWipFilter("all");
    setOutputProductFilter("");
    setReportingPointFilter("");
    setLotFilter("");
  };

  const scopeNote = (
    <>
      {dateFrom || dateTo ? `${dateFrom || "the beginning"} to ${dateTo || "today"}` : "All dates"}
      {lotFilter.trim() && <>, lot <span className="font-mono text-[#101828]">{lotFilter.trim()}</span></>}
      {exactProductMatch && <>, exactly <span className="font-mono text-[#101828]">{exactProductMatch}</span> - longer codes starting with it are different products and are not counted</>}
    </>
  );

  const exportToExcel = () => {
    let header = [];
    let dataRows = [];
    let sheetName = "Confirmed Production";
    if (view === "finished") {
      sheetName = "Finished Production";
      header = ["Lot ID", "Output Product", "Description", "Model ID", "Site", "Final Operation", "Finished Qty", "UOM", "Open Qty", "Scrap", "By-product Qty", "By-product Unit", "Confirmations", "Latest Confirmation", "WIP Clearing", "Confirmed By"];
      dataRows = finishedLots.map((l) => [
        l.production_lot_id, l.main_output_product || "—", l.main_output_product_description || "—", l.production_model_id || "—",
        l.site_id || "—", `${l.finalOperationId} - ${l.finalOperationName}`, l.quantity, l.uom, l.openQuantity ?? "—", l.scrap,
        l.byproduct || "—", l.byproductUom || "—", l.confirmations, formatAt(l.latestAt),
        !l.wipClearing ? "—" : l.wipClearing.skipped ? "Pending" : l.wipClearing.success ? "Cleared" : "Error", l.actor || "—",
      ]);
    } else if (view === "operations") {
      sheetName = "Operation Summary";
      header = ["Output Product", "Lot ID", "Operation ID", "Operation Name", "Final Step", "Site", "Model ID", "Confirmations", "Total Qty", "UOM", "Open Qty", "Total Scrap", "Total By-product", "By-product Unit", "Latest Confirmation", "WIP Clearing"];
      dataRows = operationGroups.map((g) => [
        g.main_output_product || "—", g.production_lot_id, g.operationId, g.operationName, g.isFinal ? "Yes" : "No",
        g.site_id || "—", g.production_model_id || "—", g.confirmations, g.quantity, g.uom, g.openQuantity ?? "—", g.scrap,
        g.byproduct || "—", g.byproductUom || "—", formatAt(g.latestAt),
        !g.wipClearing ? "—" : g.wipClearing.skipped ? "Pending" : g.wipClearing.success ? "Cleared" : "Error",
      ]);
    } else {
      header = TRANSACTION_COLUMNS;
      dataRows = visibleRows.map((r) => [
        formatAt(r.at), r.production_lot_id, r.main_output_product || "—", r.main_output_product_description || "—",
        formatReportingPoint(r), r.production_model_id || "—", r.site_id || "—",
        r.confirmed_quantity, formatUnit(r.uom || undefined) || "—", r.confirmed_scrap ?? 0,
        r.byproduct_confirmed_quantity ?? "—", formatUnit(r.byproduct_unit_code) || "—",
        !r.wip_clearing ? "—" : r.wip_clearing.skipped ? "Pending" : r.wip_clearing.success ? "Posted" : "Error", r.actor || "—",
      ]);
    }
    const sheet = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    XLSX.writeFile(workbook, `${sheetName.toLowerCase().replace(/ /g, "_")}_${dateFrom || "all"}_to_${dateTo || "today"}.xlsx`);
  };

  const exportDisabled = view === "finished" ? finishedLots.length === 0 : view === "operations" ? operationGroups.length === 0 : visibleRows.length === 0;

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#F2F4F7] text-[#1D2939]">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4 flex-wrap">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Confirmed Production</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <main className="flex-1 overflow-auto p-4 space-y-4">
        <div>
          <h1 className="font-heading text-lg font-bold text-[#101828]" data-testid="confirmed-production-title">Confirmed Production</h1>
          <p className="text-sm text-[#667085] font-sans mt-0.5">
            Finished production counts the final reporting point (END) only. Opens on the last 7 days.
          </p>
        </div>

        {loadError && (
          <Alert className="bg-[#FEF3F2] border-[#FECDCA]" data-testid="load-error-banner">
            <WarningCircle size={16} className="text-[#B42318]" />
            <AlertDescription className="text-[#B42318] text-sm">{loadError}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-heading font-bold uppercase tracking-wide text-[#667085]">Date:</span>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36 bg-white h-9 text-xs" data-testid="report-date-from-input" />
          <span className="text-[#98A2B3] text-xs">to</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36 bg-white h-9 text-xs" data-testid="report-date-to-input" />
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-40 bg-white h-9 text-xs" data-testid="report-site-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sites</SelectItem>
              {knownSites.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            placeholder="Filter by Lot ID..."
            value={lotFilter}
            onChange={(e) => setLotFilter(e.target.value)}
            className="w-40 bg-white h-9 text-xs"
            data-testid="report-lot-filter-input"
          />
          <Input
            placeholder="Filter by Output Product..."
            value={outputProductFilter}
            onChange={(e) => setOutputProductFilter(e.target.value)}
            className="w-52 bg-white h-9 text-xs"
            data-testid="report-output-product-filter-input"
          />
          <Input
            placeholder="Filter by Reporting Point..."
            value={reportingPointFilter}
            onChange={(e) => setReportingPointFilter(e.target.value)}
            className="w-52 bg-white h-9 text-xs"
            data-testid="report-reporting-point-filter-input"
          />
          <Select value={byproductFilter} onValueChange={setByproductFilter}>
            <SelectTrigger className="w-44 bg-white h-9 text-xs" data-testid="report-byproduct-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">By-product: All</SelectItem>
              <SelectItem value="yes">By-product: Yes</SelectItem>
              <SelectItem value="no">By-product: No</SelectItem>
            </SelectContent>
          </Select>
          <Select value={wipFilter} onValueChange={setWipFilter}>
            <SelectTrigger className="w-36 bg-white h-9 text-xs" data-testid="report-wip-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">WIP: All</SelectItem>
              <SelectItem value="wip">WIP: Cleared</SelectItem>
              <SelectItem value="non_wip">WIP: Not Cleared</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={clearFilters} data-testid="report-clear-filters-button">
            Clear Filters
          </Button>
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={loadReport} data-testid="report-refresh-button">
            <ArrowClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
          <div className="flex-1" />
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={exportToExcel} disabled={exportDisabled} data-testid="report-export-excel-button">
            <DownloadSimple size={14} className="mr-1.5" /> Export Excel
          </Button>
        </div>

        {loading ? (
          <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-sm" />)}</div>
        ) : (
          <>
            <ConfirmedProductionKpis kpis={kpis} scope={scopeNote} />

            {truncated && (
              <Alert className="bg-[#FFFAEB] border-[#FEDF89]" data-testid="truncated-banner">
                <WarningCircle size={16} className="text-[#B54708]" />
                <AlertDescription className="text-[#B54708] text-sm">
                  Only the {rows.length.toLocaleString("en-IN")} most recent confirmations in this range were loaded, so these totals are incomplete - narrow the date range or pick a site.
                </AlertDescription>
              </Alert>
            )}

            <Tabs value={view} onValueChange={setView}>
              <TabsList className="h-auto flex-wrap justify-start" data-testid="confirmed-report-view-tabs">
                <TabsTrigger value="finished" data-testid="view-tab-finished">Finished Production</TabsTrigger>
                <TabsTrigger value="operations" data-testid="view-tab-operations">Operation-wise</TabsTrigger>
                <TabsTrigger value="transactions" data-testid="view-tab-transactions">Transaction History</TabsTrigger>
              </TabsList>
            </Tabs>

            {(view === "finished" || view === "operations") && (
              <p className="text-[11px] text-[#667085] -mt-1" data-testid="open-qty-as-of">
                <strong>Open Qty</strong> is what SAP still had open at that step
                {openAsOf ? <> as of {formatAt(openAsOf)}</> : <> (SAP has not been read yet)</>}, less anything confirmed here since.
                A dash means SAP has not reported that lot since then.
              </p>
            )}

            {view === "finished" && <FinishedProductionTable lots={finishedLots} reportingPointFilter={reportingPointFilter} />}
            {view === "operations" && <OperationSummaryTable groups={operationGroups} />}
            {view === "transactions" && (
              <div className="border border-[#D0D5DD] rounded-sm overflow-auto bg-white">
                <table className="w-full text-[13px] border-collapse" data-testid="confirmed-production-table">
                  <thead>
                    <tr>
                      {TRANSACTION_COLUMNS.map((h) => (
                        <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((r, i) => (
                      <tr key={`${r.production_lot_id}-${r.at}-${i}`} className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`confirmed-report-row-${i}`}>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap text-[#475467]">{formatAt(r.at)}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium text-[#101828]">{r.production_lot_id}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5">{r.main_output_product || "—"}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{r.main_output_product_description || "—"}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">
                          {formatReportingPoint(r)}
                          {isFinalConfirmation(r) && <Badge variant="outline" className="ml-1.5 bg-[#F0FDF9] text-[#0E7C86] border-[#A6F4C5] text-[10px]">Final</Badge>}
                        </td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{r.production_model_id || "—"}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{r.site_id || "—"}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(r.confirmed_quantity)}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{formatUnit(r.uom) || "—"}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(r.confirmed_scrap)}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{r.byproduct_confirmed_quantity != null ? formatQty(r.byproduct_confirmed_quantity) : "—"}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{formatUnit(r.byproduct_unit_code) || "—"}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5"><WipBadge wip={r.wip_clearing} /></td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{r.actor || "—"}</td>
                      </tr>
                    ))}
                    {visibleRows.length === 0 && (
                      <tr>
                        <td colSpan={TRANSACTION_COLUMNS.length} className="border border-[#D0D5DD]">
                          <EmptyReportState
                            loadedCount={rows.length}
                            productFilter={outputProductFilter}
                            rpFilter={reportingPointFilter}
                            lotFilter={lotFilter}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-[11px] text-[#667085]">
              {view === "finished" && "One row per production lot, counted at the final reporting point SAP marks END. Open a lot to cross-check it against SAP and see each confirmation posted at that final step."}
              {view === "operations" && "Repeated confirmations of the same operation on the same lot are summed into one row - open a row to see each original transaction."}
              {view === "transactions" && "Every individual confirmation posted to SAP, exactly as recorded."}
            </p>
          </>
        )}
      </main>
    </div>
  );
}
